import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart as RechartsBarChart,
} from "recharts";

const COLORS = [
  "#4CAF50",
  "#FF5252",
  "#FFC107",
  "#2196F3",
  "#9C27B0",
  "#FF9800",
  "#795548",
  "#607D8B",
  "#E91E63",
  "#00BCD4",
];

const ExpenseCharts = ({
  monthlyTrendData,
  categoryDistributionData,
  expensesByCategory,
  formatCurrency,
  trendLabel,
}) => {
  // Validate props
  if (
    !monthlyTrendData ||
    !Array.isArray(monthlyTrendData) ||
    !categoryDistributionData ||
    !Array.isArray(categoryDistributionData)
  ) {
    return (
      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-lg">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg
              className="h-5 w-5 text-yellow-400"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700">
              No data available for charts
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Prepare data for the detailed breakdown chart (no normalization)
  const detailedBreakdownData = expensesByCategory.map((item, index) => ({
    name: item.name,
    amount: item.value, // Use value as provided by backend
    percentage: (
      (item.value /
        expensesByCategory.reduce((sum, curr) => sum + curr.value, 0)) *
      100
    ).toFixed(1),
    color: COLORS[index % COLORS.length],
  }));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Bar Chart */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {trendLabel}
        </h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyTrendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280" }}
              />
              <YAxis stroke="#6B7280" tick={{ fill: "#6B7280" }} />
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => `Month: ${label}`}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Legend
                wrapperStyle={{
                  paddingTop: "1rem",
                  color: "#6B7280",
                }}
              />
              <Bar dataKey="Income" fill="#4CAF50" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Expenses" fill="#FF5252" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie Chart */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Expense Distribution
        </h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={categoryDistributionData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={100}
                dataKey="value"
                label={({ name, percent }) =>
                  `${name} ${(percent * 100).toFixed(0)}%`
                }
              >
                {categoryDistributionData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                    stroke="white"
                    strokeWidth={2}
                  />
                ))}
              </Pie>
              <Tooltip
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => `Category: ${label}`}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Detailed Breakdown Chart */}
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Expense Categories
        </h3>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <RechartsBarChart data={detailedBreakdownData} layout="vertical">
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="#E5E7EB"
                horizontal={false}
              />
              <XAxis
                type="number"
                stroke="#6B7280"
                tick={{ fill: "#6B7280" }}
                tickFormatter={(value) => formatCurrency(value)}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#6B7280"
                tick={{ fill: "#6B7280" }}
                width={100}
              />
              <Tooltip
                formatter={(value, name) => [
                  name === "amount" ? formatCurrency(value) : `${value}%`,
                  name === "amount" ? "Amount" : "Percentage",
                ]}
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "0.5rem",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
              />
              <Bar
                dataKey="amount"
                fill="#4CAF50"
                radius={[0, 4, 4, 0]}
                label={{
                  position: "right",
                  formatter: (value) => formatCurrency(value),
                  fill: "#6B7280",
                  fontSize: 12,
                }}
              >
                {detailedBreakdownData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Bar>
            </RechartsBarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCharts;
